import AccommodationAmenitiesAndPoliciesContent from 'components/AccommodationKit/AccommodationAmenitiesAndPoliciesContent'
import AccommodationPackageInclusionsList from 'components/AccommodationKit/AccommodationPackageInclusionsList'
import AccommodationRoomDetailsList from 'components/AccommodationKit/AccommodationRoomDetailsList'
import FormatCurrency from 'components/Common/FormatCurrency'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import ImageGallery from 'LuxKit/Carousel/ImageGallery'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import type { ModalFooterActionProps } from 'LuxKit/Modal/ModalFooter'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import moment from 'moment'
import React, { useCallback, useMemo } from 'react'

const IMAGE_PARAMS: ImageParams = {
  mobileAspectRatio: '16:8',
  tabletAspectRatio: '16:8',
  quality: 'good',
}

interface Props {
  upgradeType: App.LoyaltyStayUpgradeType
  originPackage: App.HotelPackage
  targetPackage: App.HotelPackage
  offer?: App.Offer | App.HotelOffer | App.VillaOffer | App.BundleOffer
  checkInDate?: string
  checkOutDate?: string
  hasApplyAction?: boolean
  hasRemoveAction?: boolean
  hasBackAction?: boolean
}

export interface LoyaltyStayUpgradeDetailsModalResult {
  upgradeType: App.LoyaltyStayUpgradeType | undefined
}

function LoyaltyStayUpgradeDetailsModal({
  upgradeType,
  originPackage,
  targetPackage,
  offer,
  checkInDate,
  checkOutDate,
  hasApplyAction,
  hasRemoveAction,
  hasBackAction,
}: Props) {
  const modalElement = useModalElementContext<LoyaltyStayUpgradeDetailsModalResult | undefined>()
  const checkIn = useMemo(() => checkInDate ? moment(checkInDate) : undefined, [checkInDate])
  const checkOut = useMemo(() => checkOutDate ? moment(checkOutDate) : undefined, [checkOutDate])
  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(targetPackage, { checkIn, checkOut })

  const dismiss = useCallback(() => { modalElement.resolve(undefined) }, [modalElement])

  const handleApplyUpgrade = useCallback(() => {
    modalElement.resolve({ upgradeType })
  }, [modalElement, upgradeType])

  const handleRemoveUpgrade = useCallback(() => {
    modalElement.resolve({ upgradeType: undefined })
  }, [modalElement])

  const primaryActionProps = useMemo((): ModalFooterActionProps | undefined => {
    if (!hasApplyAction) return
    return {
      children: `Apply free ${upgradeType} upgrade`,
      onClick: handleApplyUpgrade,
    }
  }, [upgradeType, handleApplyUpgrade, hasApplyAction])

  const secondaryActionProps = useMemo((): ModalFooterActionProps | undefined => {
    if (!hasRemoveAction) return
    return {
      children: 'Remove',
      onClick: handleRemoveUpgrade,
    }
  }, [hasRemoveAction, handleRemoveUpgrade])

  return <ModalBase size="L">
    <ModalHeader
      title={<>
        {upgradeType === 'room' && 'Free room upgrade'}
        {upgradeType === 'package' && 'Free package upgrade'}
      </>}
      dismissible={!hasBackAction}
      onBackButtonClick={hasBackAction ? dismiss : undefined}
    />
    <ModalBody>
      {!!targetPackage.roomType.images.length && <ImageGallery
        images={targetPackage.roomType.images}
        imageParams={IMAGE_PARAMS}
      />}
      <ModalContent>
        <Group direction="vertical" gap={16}>
          <div>
            {upgradeType === 'room' && <>
              <Heading variant="heading4" as="s" colour="neutral-four">{originPackage.roomType.name}</Heading>
              <Heading variant="heading4" colour="neutral-two">Upgrade to {targetPackage.roomType.name}</Heading>
            </>}
            {upgradeType === 'package' && <>
              <Heading variant="heading4" as="s" colour="neutral-four">{originPackage.name}</Heading>
              <Heading variant="heading4" colour="neutral-two">Upgrade to {targetPackage.name}</Heading>
            </>}
            <BodyText variant="medium" colour="highlight-secondary">
              Valued up to <b><FormatCurrency value={targetPackage.value} /></b>
            </BodyText>
          </div>
          <Group direction="vertical" gap={20}>
            {upgradeType === 'room' && <>
              <BodyText variant="large">{targetPackage.roomType.description}</BodyText>
              <AccommodationRoomDetailsList roomPackage={targetPackage} />
              <AccommodationAmenitiesAndPoliciesContent roomPackage={targetPackage} />
            </>}
            {upgradeType === 'package' && <Group direction="vertical" gap={8}>
              {!!luxPlusInclusions.length && <LuxPlusInclusions
                luxPlusInclusions={luxPlusInclusions}
                accommodationOffer={offer}
                hideUpsellModal
              />}
              {!!bonusInclusions.length && <AccommodationPackageInclusionsList title="bonus">
                {bonusInclusions.map((inclusion) => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} size="medium" />)}
              </AccommodationPackageInclusionsList>}
              <AccommodationPackageInclusionsList title={bonusInclusions.length ? 'additional' : undefined}>
                {inclusions.map((inclusion) => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} size="medium" />)}
              </AccommodationPackageInclusionsList>
            </Group>}
          </Group>
        </Group>
      </ModalContent>
    </ModalBody>
    <ModalFooter
      primaryActionProps={primaryActionProps}
      secondaryActionProps={secondaryActionProps}
    />
  </ModalBase>
}

export default LoyaltyStayUpgradeDetailsModal
